/**
 * 检索要素API接口定义
 */

/**
 * 表示关键词或IPC项的数据结构
 */
export interface ItemWithSource {
    /**
     * 文本内容（关键词或IPC分类号）
     */
    key: string;
    /**
     * 数据来源标识
     */
    source: string;
}

/**
 * 表示检索要素中的表达式数据结构
 */
export interface RetrievalElementExpress {
    /**
     * 关键词
     */
    word: ItemWithSource;
    /**
     * 拓展词列表
     */
    extend: ItemWithSource[];
    /**
     * IPC分类号列表
     */
    ipc: ItemWithSource[];
}

/**
 * 表示检索要素数据结构
 */
export interface RetrievalElement {
    /**
     * 是否选中
     */
    selected: boolean;
    /**
     * 已勾选的特征
     */
    feature_text_original: string;
    /**
     * 特征类型
     * topic_search - 主题要素
     * key_search - 关键要素
     * sup_search - 补充要素
     * '' - 空(代表用户没有选择技术特征)
     */
    feature_type: 'topic_search' | 'key_search' | 'sup_search';
    /**
     * 表达式列表
     */
    express: RetrievalElementExpress[];
}

/**
 * 获取检索要素响应数据
 */
export interface GetRetrievalElementsResponse {
    /**
     * 检索要素列表
     */
    list: RetrievalElement[];
    family_merge_enabled: boolean;
}

/**
 * 异步获取检索要素响应数据
 */
export interface GetRetrievalElementsAsyncResponse {
    /**
     * 任务状态：SUCCESS, FAILED, RUNNING
     */
    status: 'SUCCESS' | 'FAILED' | 'RUNNING';
    /**
     * 检索要素数据（当 status 为 SUCCESS 时返回）
     */
    result?: GetRetrievalElementsResponse;
    /**
     * 错误信息（当 status 为 FAILED 时返回）
     */
    error_message?: string;
    /**
     * 任务ID
     */
    task_id: string;
}

/**
 * 更新检索要素请求数据
 */
export interface UpdateRetrievalElementsRequest {
    /**
     * 任务ID
     */
    task_id: string;
    /**
     * 检索要素列表
     */
    data: RetrievalElement[];
    /**
     * 是否启用同族合并
     */
    family_merge_enabled?: boolean;
}

/**
 * 更新检索要素响应数据
 */
export interface UpdateRetrievalElementsResponse {
    /**
     * 是否成功
     */
    success: boolean;
}

/**
 * 扩展检索要素请求数据
 */
export interface ExtendRetrievalElementRequest {
    /**
     * 任务ID
     */
    task_id: string;
    /**
     * 需要扩展的文本
     */
    add_word: string;
}

/**
 * 扩展检索要素响应数据
 */
export interface ExtendRetrievalElementResponse {
    /**
     * 扩展词列表
     */
    extend_list: string[];
    /**
     * IPC分类号列表
     */
    ipc: string[];
}

/**
 * 检索要素API接口
 */
export const RetrievalElementsApi = Symbol.for('RetrievalElementsApi');

export interface RetrievalElementsApi {
    /**
     * 获取检索要素
     * @param taskId 任务ID
     * @param signal abort 信号
     * @returns 异步检索要素数据
     */
    getRetrievalElements(taskId: string, signal?: AbortSignal): Promise<GetRetrievalElementsAsyncResponse>;

    /**
     * 更新检索要素
     * @param data 更新检索要素请求数据
     * @returns 更新结果
     */
    updateRetrievalElements(data: UpdateRetrievalElementsRequest): Promise<UpdateRetrievalElementsResponse>;

    /**
     * 扩展单个检索要素
     * @param data 扩展检索要素请求数据
     * @returns 扩展结果
     */
    extendRetrievalElement(data: ExtendRetrievalElementRequest): Promise<ExtendRetrievalElementResponse>;
}
